import type RouterService from '@ember/routing/router-service'
import Service, { service } from '@ember/service'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'

interface RolloverLockConfig {
  subco?: {
    lockCurrentSubscriptionType?: boolean
    lockOtherSubscriptionType?: boolean
  }
  teacher?: {
    lockCurrentSubscriptionType?: boolean
    lockOtherSubscriptionType?: boolean
  }
}

export interface HasRolloverLockConfig {
  rolloverLock?: RolloverLockConfig
}

/**
 * @class SchoolRolloverLockService
 * @description to lock a route during school rollover, add one or more of the
 * following `rolloverLock` entries to the route's metadata:
 *
 * ```js
 * buildRouteInfoMetadata() {
 *  return {
 *    rolloverLock: {
 *      subco: {
 *        // lock subco from route when active rollover equals current subscription type
 *        lockCurrentSubscriptionType: true,
 *        // lock subco from route when active rollover equals opposite subscription type
 *        lockOtherSubscriptionType: true,
 *      },
 *      teacher: {
 *        // lock teacher from route when active rollover equals current subscription type
 *        lockCurrentSubscriptionType: true,
 *        // lock teacher from route when active rollover equals opposite subscription type
 *        lockOtherSubscriptionType: true,
 *      },
 *    },
 *  }
 * },
 * ```
 *
 * the appropriately placed <Rollover::Lock::Overlay> and <Rollover::Lock::Alert>
 * components will use this service's public computed properties to determine
 * if they should be displayed and what content they will contain.
 */
export default class SchoolRolloverLockService extends Service {
  @service router!: RouterService

  @service schoolRollover!: SchoolRolloverService

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  get isCurrentRouteLocked() {
    return Boolean(this.lockingRollover)
  }

  get lockingRollover() {
    const currentSubscriptionType = this.subscriptionType.current
    if (currentSubscriptionType === undefined) return

    const isSubcoForSubscriptionType = this.session.currentTeacher.subcoFor(currentSubscriptionType)

    const userRole = isSubcoForSubscriptionType ? 'subco' : 'teacher'

    const { activeRollovers } = this.schoolRollover

    const currentSubscriptionTypeRollover = activeRollovers?.find(
      ({ subscriptionType }) => subscriptionType === currentSubscriptionType,
    )
    const otherSubscriptionTypeRollover = activeRollovers?.find(({ subscriptionType }) =>
      this.subscriptionType.others?.includes(subscriptionType),
    )

    if (
      currentSubscriptionTypeRollover &&
      this.router.currentRoute?.find((routeInfo) => {
        const hasRolloverLockConfig = routeInfo?.metadata as HasRolloverLockConfig
        return hasRolloverLockConfig?.rolloverLock?.[userRole]?.lockCurrentSubscriptionType === true
      })
    )
      return currentSubscriptionTypeRollover
    if (
      otherSubscriptionTypeRollover &&
      this.router.currentRoute?.find((routeInfo) => {
        const hasRolloverLockConfig = routeInfo?.metadata as HasRolloverLockConfig
        return hasRolloverLockConfig?.rolloverLock?.[userRole]?.lockOtherSubscriptionType === true
      })
    )
      return otherSubscriptionTypeRollover

    return undefined
  }
}

declare module '@ember/service' {
  interface Registry {
    'school-rollover-lock': SchoolRolloverLockService
  }
}
